import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { cleanCode, stepLabel, when } from "@/lib/shipments";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Eyebrow } from "@/components/site/Reveal";
import { TrackSteps } from "@/components/site/track/TrackSteps";
import { TrackTimeline } from "@/components/site/track/TrackTimeline";
import { TrackCorreios } from "@/components/site/track/TrackCorreios";
import { TrackAlerts } from "@/components/site/track/TrackAlerts";

const CodeForm = ({ initial }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState(initial || "");
  const submit = (e) => {
    e.preventDefault();
    if (code.length >= 8) navigate(`/rastreio/${code}`);
  };
  return (
    <form data-testid="track-form" onSubmit={submit} className="flex max-w-xl gap-2 rounded-full border border-white/15 bg-white/[0.04] p-1.5">
      <input data-testid="track-code-input" value={code} onChange={(e) => setCode(cleanCode(e.target.value))} placeholder="Seu código (ex: RC7K2P9Q)" aria-label="Código do pedido"
        autoCapitalize="characters" autoComplete="off" className="mono min-w-0 flex-1 bg-transparent px-4 text-base tracking-wider text-white outline-none placeholder:text-zinc-500" />
      <button type="submit" data-testid="track-submit" disabled={code.length < 8}
        className="flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.03] disabled:opacity-40"><Search className="h-4 w-4" /> Rastrear</button>
    </form>
  );
};

const Result = ({ data }) => (
  <div data-testid="track-result" className="space-y-6">
    <div className="rounded-[24px] border border-white/10 bg-[#111113] p-5 sm:p-7">
      <p className="text-sm text-zinc-400">{data.first_name ? `Olá, ${data.first_name}! ` : ""}Pedido <span className="mono text-white">{data.code}</span></p>
      <p data-testid="track-item" className="font-display mt-2 text-2xl font-bold tracking-[-0.02em] text-white sm:text-3xl">{data.item}</p>
      <p data-testid="track-status" className="mt-2 text-sm text-zinc-300">Agora: <strong className="text-white">{stepLabel(data.status) || "Aguardando"}</strong>{data.updated_at ? ` · atualizado em ${when(data.updated_at)}` : ""}</p>
      <div className="mt-6"><TrackSteps status={data.status} /></div>
    </div>
    <TrackCorreios code={data.tracking_code} />
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <div className="rounded-[24px] border border-white/10 p-5 sm:p-7">
        <p className="mb-5 text-sm font-semibold text-white">Histórico</p>
        <TrackTimeline events={data.events} />
      </div>
      <TrackAlerts code={data.code} />
    </div>
  </div>
);

export default function Track() {
  const { code: raw } = useParams();
  const code = cleanCode(raw || "");
  const [state, setState] = useState({ data: null, error: "", loading: false });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Rastrear pedido | RC iPhones";
  }, []);

  useEffect(() => {
    if (!code) { setState({ data: null, error: "", loading: false }); return undefined; }
    let alive = true;
    const load = (quiet) => {
      if (!quiet) setState({ data: null, error: "", loading: true });
      api.get(`/track/${code}`)
        .then((r) => alive && setState({ data: r?.data || null, error: "", loading: false }))
        .catch((e) => alive && !quiet && setState({ data: null, error: formatApiError(e), loading: false }));
    };
    load(false);
    const t = setInterval(() => load(true), 60000);
    return () => { alive = false; clearInterval(t); };
  }, [code]);

  return (
    <SmoothScroll>
      <div className="grain min-h-screen bg-[#09090b] text-white">
        <Header />
        <main data-testid="track-page" className="mx-auto max-w-[1440px] px-5 pb-24 pt-[136px] sm:px-8 lg:pt-32">
          <div className="max-w-2xl">
            <Eyebrow>Rastreio</Eyebrow>
            <h1 className="font-display mt-5 text-4xl font-extrabold leading-[0.95] tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
              Acompanhe sua <span className="text-outline">entrega.</span>
            </h1>
            <p className="mt-5 text-base text-zinc-300">Digite o código que a RC iPhones te enviou no WhatsApp para ver onde está o seu pedido.</p>
          </div>
          <div className="mt-8"><CodeForm key={code} initial={code} /></div>
          <div className="mt-10">
            {state.loading && <p data-testid="track-loading" className="text-sm text-zinc-400">Buscando seu pedido…</p>}
            {state.error && <p data-testid="track-error" className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{state.error}</p>}
            {state.data && <Result data={state.data} />}
          </div>
        </main>
        <Footer />
      </div>
    </SmoothScroll>
  );
}
